import React, { useState, useEffect } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { Card } from "primereact/card";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";

const Services = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(5);
  const [intervalUnit, setIntervalUnit] = useState("min"); // 'sec' or 'min'
  const [services, setServices] = useState({
    universal: {
      waterMeterService: {
        status: "checking",
        responseTime: null,
        lastChecked: null,
      },
      powerMeterService: {
        status: "checking",
        responseTime: null,
        lastChecked: null,
      },
      powerMeterBilling: {
        status: "checking",
        responseTime: null,
        lastChecked: null,
      },
      waterMeterBilling: {
        status: "checking",
        responseTime: null,
        lastChecked: null,
      },
      acrelMeters: {
        status: "checking",
        responseTime: null,
        lastChecked: null,
      },
      tcpServer: { status: "checking", responseTime: null, lastChecked: null },
      messagingService: {
        status: "checking",
        responseTime: null,
        lastChecked: null,
      },
    },
    payserve: {
      mpesaService: {
        status: "checking",
        responseTime: null,
        lastChecked: null,
      },
      backendService: {
        status: "checking",
        responseTime: null,
        lastChecked: null,
      },
      invoiceService: {
        status: "checking",
        responseTime: null,
        lastChecked: null,
      },
    },
    swan: {
      mpesaService: {
        status: "checking",
        responseTime: null,
        lastChecked: null,
      },
      backendService: {
        status: "checking",
        responseTime: null,
        lastChecked: null,
      },
      invoiceService: {
        status: "checking",
        responseTime: null,
        lastChecked: null,
      },
    },
    upay: {
      mpesaService: {
        status: "checking",
        responseTime: null,
        lastChecked: null,
      },
      backendService: {
        status: "checking",
        responseTime: null,
        lastChecked: null,
      },
      invoiceService: {
        status: "checking",
        responseTime: null,
        lastChecked: null,
      },
    },
  });

  // Generate dummy data for inactive services
  const generateDummyData = () => {
    const dummyStatuses = ["healthy", "slow", "error", "unreachable"];
    const getRandomStatus = () =>
      dummyStatuses[Math.floor(Math.random() * dummyStatuses.length)];
    const getRandomResponseTime = (status) => {
      if (status === "healthy") return Math.floor(Math.random() * 800) + 100;
      if (status === "slow") return Math.floor(Math.random() * 2000) + 1000;
      return null;
    };

    const createDummyService = () => {
      const status = getRandomStatus();
      return {
        status,
        responseTime: getRandomResponseTime(status),
        lastChecked: new Date().toISOString(),
      };
    };

    return {
      universal: {
        waterMeterService: createDummyService(),
        powerMeterService: createDummyService(),
        powerMeterBilling: createDummyService(),
        waterMeterBilling: createDummyService(),
        acrelMeters: createDummyService(),
        tcpServer: createDummyService(),
        messagingService: createDummyService(),
      },
      payserve: {
        mpesaService: createDummyService(),
        backendService: createDummyService(),
        invoiceService: createDummyService(),
      },
      swan: {
        mpesaService: createDummyService(),
        backendService: createDummyService(),
        invoiceService: createDummyService(),
      },
      upay: {
        mpesaService: createDummyService(),
        backendService: createDummyService(),
        invoiceService: createDummyService(),
      },
    };
  };

  // Service health check function
  const checkServiceHealth = async (url, timeout = 5000) => {
    const startTime = Date.now();
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(url, {
        method: "GET",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
        },
      });

      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;

      if (response.ok) {
        return {
          status: responseTime < 1000 ? "healthy" : "slow",
          responseTime,
          lastChecked: new Date().toISOString(),
        };
      } else {
        return {
          status: "error",
          responseTime: null,
          lastChecked: new Date().toISOString(),
        };
      }
    } catch (error) {
      return {
        status: "unreachable",
        responseTime: null,
        lastChecked: new Date().toISOString(),
      };
    }
  };

  // Check all services
  const checkAllServices = async () => {
    setRefreshing(true);

    // Universal Services
    const universalChecks = {
      waterMeterService: checkServiceHealth("/api/water-meter/health"),
      powerMeterService: checkServiceHealth("/api/power-meter/health"),
      powerMeterBilling: checkServiceHealth("/api/power-meter/billing/health"),
      waterMeterBilling: checkServiceHealth("/api/water-meter/billing/health"),
      acrelMeters: checkServiceHealth("/api/acrel-meters/health"),
      tcpServer: checkServiceHealth("/api/tcp-server/health"),
      messagingService: checkServiceHealth("/api/messaging/health"),
    };

    // PayServe Services
    const payserveChecks = {
      mpesaService: checkServiceHealth("/api/payserve/mpesa/health"),
      backendService: checkServiceHealth("/api/payserve/backend/health"),
      invoiceService: checkServiceHealth("/api/payserve/invoice/health"),
    };

    // Swan Services
    const swanChecks = {
      mpesaService: checkServiceHealth("/api/swan/mpesa/health"),
      backendService: checkServiceHealth("/api/swan/backend/health"),
      invoiceService: checkServiceHealth("/api/swan/invoice/health"),
    };

    // Upay Services
    const upayChecks = {
      mpesaService: checkServiceHealth("/api/upay/mpesa/health"),
      backendService: checkServiceHealth("/api/upay/backend/health"),
      invoiceService: checkServiceHealth("/api/upay/invoice/health"),
    };

    try {
      const [universal, payserve, swan, upay] = await Promise.all([
        Promise.all(
          Object.entries(universalChecks).map(async ([key, promise]) => [
            key,
            await promise,
          ]),
        ),
        Promise.all(
          Object.entries(payserveChecks).map(async ([key, promise]) => [
            key,
            await promise,
          ]),
        ),
        Promise.all(
          Object.entries(swanChecks).map(async ([key, promise]) => [
            key,
            await promise,
          ]),
        ),
        Promise.all(
          Object.entries(upayChecks).map(async ([key, promise]) => [
            key,
            await promise,
          ]),
        ),
      ]);

      setServices({
        universal: Object.fromEntries(universal),
        payserve: Object.fromEntries(payserve),
        swan: Object.fromEntries(swan),
        upay: Object.fromEntries(upay),
      });
    } catch (error) {
      console.error("Error checking services:", error);
      toastify("Error checking service health", "error");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // Use dummy data for demonstration
    setServices(generateDummyData());

    // Calculate interval in milliseconds
    const intervalMs =
      intervalUnit === "sec"
        ? refreshInterval * 1000
        : refreshInterval * 60 * 1000;

    // Set up auto-refresh
    const interval = setInterval(() => {
      setServices(generateDummyData());
      // Uncomment below to use real API calls instead of dummy data
      // checkAllServices();
    }, intervalMs);

    return () => clearInterval(interval);
  }, [refreshInterval, intervalUnit]);

  // Service Card Component
  const ServiceCard = ({ title, service, icon }) => {
    const getStatusConfig = (status) => {
      switch (status) {
        case "healthy":
          return {
            color: "success",
            icon: "ti ti-circle-check",
            text: "Healthy",
            bgClass: "bg-light-success",
          };
        case "slow":
          return {
            color: "warning",
            icon: "ti ti-alert-triangle",
            text: "Slow Response",
            bgClass: "bg-light-warning",
          };
        case "error":
          return {
            color: "danger",
            icon: "ti ti-circle-x",
            text: "Error",
            bgClass: "bg-light-danger",
          };
        case "unreachable":
          return {
            color: "danger",
            icon: "ti ti-plug-x",
            text: "Unreachable",
            bgClass: "bg-light-danger",
          };
        case "checking":
        default:
          return {
            color: "secondary",
            icon: "ti ti-dots",
            text: "Checking...",
            bgClass: "bg-light-secondary",
          };
      }
    };

    const statusConfig = getStatusConfig(service.status);

    return (
      <div className="col-md-4 col-sm-6 mb-3">
        <div
          className={`card border-0 shadow-sm border-start border-${statusConfig.color} border-3`}
        >
          <div className="card-body">
            <div className="d-flex align-items-center mb-3">
              <div
                className={`flex-shrink-0 ${statusConfig.bgClass} p-3 rounded`}
              >
                <i
                  className={`${icon} text-${statusConfig.color}`}
                  style={{ fontSize: "24px" }}
                ></i>
              </div>
              <div className="flex-grow-1 ms-3">
                <h6 className="mb-1 fw-bold">{title}</h6>
                <div className="d-flex align-items-center">
                  <i
                    className={`${statusConfig.icon} text-${statusConfig.color} me-1`}
                  ></i>
                  <span className={`text-${statusConfig.color} fw-semibold`}>
                    {statusConfig.text}
                  </span>
                </div>
              </div>
            </div>

            <div className="row g-2">
              <div className="col-6">
                <div className="text-muted small">Response Time</div>
                <div className="fw-bold">
                  {service.status === "checking" ? (
                    <ProgressSpinner
                      style={{ width: "20px", height: "20px" }}
                      strokeWidth="4"
                    />
                  ) : service.responseTime !== null ? (
                    `${service.responseTime}ms`
                  ) : (
                    "N/A"
                  )}
                </div>
              </div>
              <div className="col-6">
                <div className="text-muted small">Last Checked</div>
                <div className="fw-bold small">
                  {service.lastChecked
                    ? new Date(service.lastChecked).toLocaleTimeString()
                    : "N/A"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Get overall health status
  const getOverallHealth = (serviceGroup) => {
    const statuses = Object.values(serviceGroup).map((s) => s.status);
    if (statuses.includes("unreachable") || statuses.includes("error")) {
      return {
        severity: "danger",
        text: "Critical",
        count: statuses.filter((s) => s === "unreachable" || s === "error")
          .length,
      };
    } else if (statuses.includes("slow")) {
      return {
        severity: "warning",
        text: "Warning",
        count: statuses.filter((s) => s === "slow").length,
      };
    } else if (statuses.every((s) => s === "healthy")) {
      return {
        severity: "success",
        text: "All Healthy",
        count: statuses.length,
      };
    } else {
      return { severity: "info", text: "Checking", count: 0 };
    }
  };

  // Interval Selector Component
  const IntervalSelector = () => {
    const intervals = [5, 10, 15, 30, 60];

    return (
      <div className="d-flex align-items-center gap-3">
        <span className="text-muted fw-semibold">Check every:</span>

        <div className="d-flex gap-2">
          <input
            type="number"
            className="form-control"
            style={{ width: "80px" }}
            value={refreshInterval}
            onChange={(e) =>
              setRefreshInterval(Math.max(1, parseInt(e.target.value) || 1))
            }
            min="1"
          />
        </div>

        <div className="d-flex gap-2">
          <div
            className={`card mb-0 cursor-pointer ${intervalUnit === "sec" ? "bg-light-success border-success" : "bg-light border"}`}
            style={{
              padding: "8px 16px",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
            onClick={() => setIntervalUnit("sec")}
          >
            <div className="text-center">
              <i className="ti ti-clock me-1"></i>
              <span
                className={`fw-semibold ${intervalUnit === "sec" ? "text-success" : "text-muted"}`}
              >
                Seconds
              </span>
            </div>
          </div>

          <div
            className={`card mb-0 cursor-pointer ${intervalUnit === "min" ? "bg-light-success border-success" : "bg-light border"}`}
            style={{
              padding: "8px 16px",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
            onClick={() => setIntervalUnit("min")}
          >
            <div className="text-center">
              <i className="ti ti-clock me-1"></i>
              <span
                className={`fw-semibold ${intervalUnit === "min" ? "text-success" : "text-muted"}`}
              >
                Minutes
              </span>
            </div>
          </div>
        </div>

        <Button
          icon="ti ti-refresh"
          className="p-button-outlined p-button-secondary"
          onClick={() => setServices(generateDummyData())}
          loading={refreshing}
          tooltip="Refresh Services Health Now"
          label="Refresh Now"
        />
      </div>
    );
  };

  return (
    <>
      <div className="row mb-3">
        <div className="col-md-12">
          <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted small">
              <i className="ti ti-info-circle me-1"></i>
              Services are automatically checked every {refreshInterval}{" "}
              {intervalUnit === "sec" ? "second(s)" : "minute(s)"}
            </div>
            <IntervalSelector />
          </div>
        </div>
      </div>

      {/* Universal Services Section */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="ti ti-server me-2"></i>Universal Services
            </h5>
            <Badge
              value={getOverallHealth(services.universal).text}
              severity={getOverallHealth(services.universal).severity}
            />
          </div>
        </div>
        <div className="card-body">
          <div className="row">
            <ServiceCard
              title="Water Meter Service"
              service={services.universal.waterMeterService}
              icon="ti ti-droplet"
            />
            <ServiceCard
              title="Power Meter Service"
              service={services.universal.powerMeterService}
              icon="ti ti-bolt"
            />
            <ServiceCard
              title="Power Meter Billing"
              service={services.universal.powerMeterBilling}
              icon="ti ti-receipt"
            />
            <ServiceCard
              title="Water Meter Billing"
              service={services.universal.waterMeterBilling}
              icon="ti ti-receipt-2"
            />
            <ServiceCard
              title="Acrel Meters"
              service={services.universal.acrelMeters}
              icon="ti ti-device-analytics"
            />
            <ServiceCard
              title="TCP Server"
              service={services.universal.tcpServer}
              icon="ti ti-router"
            />
            <ServiceCard
              title="Messaging Service"
              service={services.universal.messagingService}
              icon="ti ti-message-circle"
            />
          </div>
        </div>
      </div>

      {/* Company-Specific Services Tabs */}
      <div className="card border-0 shadow-sm">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="ti ti-building me-2"></i>Company Services
          </h5>
        </div>
        <div className="card-body">
          <TabView
            activeIndex={activeTab}
            onTabChange={(e) => setActiveTab(e.index)}
          >
            {/* PayServe Tab */}
            <TabPanel
              header={
                <div className="d-flex align-items-center">
                  <span className="me-2">PayServe</span>
                  <Badge
                    value={getOverallHealth(services.payserve).text}
                    severity={getOverallHealth(services.payserve).severity}
                    size="small"
                  />
                </div>
              }
            >
              <div className="row mt-3">
                <ServiceCard
                  title="M-Pesa Service"
                  service={services.payserve.mpesaService}
                  icon="ti ti-credit-card"
                />
                <ServiceCard
                  title="Backend Service"
                  service={services.payserve.backendService}
                  icon="ti ti-database"
                />
                <ServiceCard
                  title="Invoice Service"
                  service={services.payserve.invoiceService}
                  icon="ti ti-file-invoice"
                />
              </div>
            </TabPanel>

            {/* Swan Tab */}
            <TabPanel
              header={
                <div className="d-flex align-items-center">
                  <span className="me-2">Swan</span>
                  <Badge
                    value={getOverallHealth(services.swan).text}
                    severity={getOverallHealth(services.swan).severity}
                    size="small"
                  />
                </div>
              }
            >
              <div className="row mt-3">
                <ServiceCard
                  title="M-Pesa Service"
                  service={services.swan.mpesaService}
                  icon="ti ti-credit-card"
                />
                <ServiceCard
                  title="Backend Service"
                  service={services.swan.backendService}
                  icon="ti ti-database"
                />
                <ServiceCard
                  title="Invoice Service"
                  service={services.swan.invoiceService}
                  icon="ti ti-file-invoice"
                />
              </div>
            </TabPanel>

            {/* Upay Tab */}
            <TabPanel
              header={
                <div className="d-flex align-items-center">
                  <span className="me-2">Upay</span>
                  <Badge
                    value={getOverallHealth(services.upay).text}
                    severity={getOverallHealth(services.upay).severity}
                    size="small"
                  />
                </div>
              }
            >
              <div className="row mt-3">
                <ServiceCard
                  title="M-Pesa Service"
                  service={services.upay.mpesaService}
                  icon="ti ti-credit-card"
                />
                <ServiceCard
                  title="Backend Service"
                  service={services.upay.backendService}
                  icon="ti ti-database"
                />
                <ServiceCard
                  title="Invoice Service"
                  service={services.upay.invoiceService}
                  icon="ti ti-file-invoice"
                />
              </div>
            </TabPanel>
          </TabView>
        </div>
      </div>
    </>
  );
};

export default Services;
